// Header fields of the inspection report (zapisnik nadzora); saved 600 ms after typing, on blur and
// when the page is hidden or unloaded.
import { useEffect, useId, useRef, useState } from 'react';
import type { InspectionMeta } from '../../../shared/types';
import { onPageHide } from './helpers';
import { Icon } from './Icon';

type MetaKey = keyof InspectionMeta;

export const META_FIELDS: ReadonlyArray<{ key: MetaKey; label: string; multiline?: boolean; placeholder?: string }> = [
  { key: 'objekt', label: 'Objekt', placeholder: 'npr. Vrtec Kekec – prizidek' },
  { key: 'lokacija', label: 'Lokacija', placeholder: 'naslov ali parcela' },
  { key: 'investitor', label: 'Investitor' },
  { key: 'izvajalec', label: 'Izvajalec' },
  { key: 'nadzornik', label: 'Nadzornik' },
  { key: 'prisotni', label: 'Prisotni', multiline: true, placeholder: 'imena prisotnih, ločena z vejico' },
  { key: 'opombe', label: 'Opombe', multiline: true },
];

const SAVE_DELAY_MS = 600;

type Draft = Record<MetaKey, string>;

function toDraft(meta: InspectionMeta): Draft {
  const draft = {} as Draft;
  for (const f of META_FIELDS) draft[f.key] = meta[f.key] ?? '';
  return draft;
}

function sameDraft(a: Draft, b: Draft): boolean {
  return META_FIELDS.every((f) => a[f.key] === b[f.key]);
}

interface InspectionHeaderProps {
  meta: InspectionMeta;
  /** Receives the fields edited since the last save (an empty string clears a field on the server). */
  onSave: (meta: InspectionMeta) => void;
  /**
   * Entries exist (or one is being recorded): the form starts collapsed and collapses when the
   * first entry starts, so it does not push the entries below the dictation bar.
   */
  hasEntries?: boolean;
}

export function InspectionHeaderForm({ meta, onSave, hasEntries = false }: InspectionHeaderProps) {
  const formId = useId();
  const [draft, setDraft] = useState<Draft>(() => toDraft(meta));
  const [open, setOpen] = useState(() => !hasEntries && META_FIELDS.every((f) => !meta[f.key]));
  const saved = useRef<Draft>(toDraft(meta));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focused = useRef(false);
  const latest = useRef({ draft, onSave });
  useEffect(() => {
    latest.current = { draft, onSave };
  });

  const flush = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const { draft: current, onSave: save } = latest.current;
    // Only what was edited here: the shown copy may lack a value set meanwhile (on another device,
    // or after the offline copy was saved), and the server deletes a field that is sent empty.
    const changed: InspectionMeta = {};
    for (const f of META_FIELDS) if (current[f.key] !== saved.current[f.key]) changed[f.key] = current[f.key];
    if (Object.keys(changed).length === 0) return;
    saved.current = current;
    save(changed);
  };

  // Server/optimistic updates replace the form only while nobody is typing in it.
  useEffect(() => {
    const incoming = toDraft(meta);
    if (focused.current || timer.current !== null) return;
    saved.current = incoming;
    setDraft((cur) => (sameDraft(cur, incoming) ? cur : incoming));
  }, [meta]);

  // Pending changes are written when the page is left, hidden or unloaded.
  useEffect(() => {
    const stop = onPageHide(flush);
    return () => {
      stop();
      flush();
    };
  }, []);

  useEffect(() => {
    if (!hasEntries || focused.current) return;
    flush();
    setOpen(false);
  }, [hasEntries]);

  const update = (key: MetaKey, value: string) => {
    setDraft((cur) => ({ ...cur, [key]: value }));
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY_MS);
  };

  const summary = [draft.objekt, draft.lokacija].filter(Boolean).join(' · ');

  return (
    <section className="card meta-form">
      <button
        type="button"
        className="meta-form__toggle"
        aria-expanded={open}
        aria-controls={formId}
        onClick={() => {
          if (open) flush();
          setOpen(!open);
        }}
      >
        <span>
          <span className="card__title">Podatki o nadzoru</span>
          {!open && <span className="meta-form__summary">{summary || 'Objekt, lokacija, investitor, izvajalec …'}</span>}
        </span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} />
      </button>
      {open && (
        <div id={formId} className="meta-form__fields">
          {META_FIELDS.map((f) => {
            const id = `${formId}-${f.key}`;
            const common = {
              id,
              value: draft[f.key],
              placeholder: f.placeholder,
              maxLength: 2000,
              onFocus: () => {
                focused.current = true;
              },
              onBlur: () => {
                focused.current = false;
                flush();
              },
            };
            return (
              <div key={f.key} className={`field${f.multiline ? ' field--wide' : ''}`}>
                <label htmlFor={id}>{f.label}</label>
                {f.multiline ? (
                  <textarea {...common} rows={2} onChange={(e) => update(f.key, e.target.value)} />
                ) : (
                  <input {...common} type="text" autoComplete="off" onChange={(e) => update(f.key, e.target.value)} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
